import { useMemo } from 'react';
import { InvoiceStatus, InvoiceClassification } from '@config/invoice-config';
import { UserRole, MANDATORY_ASSIGNMENT_STATUSES } from '@config/workflow-config';
import {
  isFieldVisible,
  isFieldEditable,
  getVisibleFields,
  getEditableFields,
  getFieldPermissions,
  type FieldPermission,
  type WorkflowUIField
} from '@config/workflow-field-permissions';

export interface WorkflowPermissions {
  isFieldVisible: (field: WorkflowUIField) => boolean;
  isFieldEditable: (field: WorkflowUIField) => boolean;
  getFieldPermissions: (field: WorkflowUIField) => FieldPermission;
  getVisibleFields: () => WorkflowUIField[];
  getEditableFields: () => WorkflowUIField[];
  debug: {
    userRoles: string[];
    visibleFields: string[];
    editableFields: string[];
    requiredFields: string[];
  };
}

export function useWorkflowPermissions(
  currentStatus: InvoiceStatus,
  _currentClassification: InvoiceClassification,
  userRole: UserRole,
  currentUserId?: string,
  assignedToUserId?: string
): WorkflowPermissions {
  return useMemo(() => {

    // Helper functions
    const isFieldVisibleForRole = (field: WorkflowUIField): boolean => {
      return isFieldVisible(currentStatus, userRole, field);
    };

    const isFieldEditableForRole = (field: WorkflowUIField): boolean => {
      // First check the base permission
      const basePermission = isFieldEditable(currentStatus, userRole, field);
      if (!basePermission) return false;

      // For mandatory assignment statuses (steps 2, 4, 5, 8), user must be assigned to edit ANY field
      if (MANDATORY_ASSIGNMENT_STATUSES.some(status => status === currentStatus)) {
        // User must be assigned to the invoice to edit fields
        const isAssigned = currentUserId && assignedToUserId && currentUserId === assignedToUserId;
        if (!isAssigned) {
          // Assignment is priority - no fields are editable until user assigns to themselves
          return false;
        }
      }

      return true;
    };

    const getFieldPermissionsForRole = (field: WorkflowUIField): FieldPermission => {
      return getFieldPermissions(currentStatus, userRole, field);
    };

    const getVisibleFieldsForRole = (): WorkflowUIField[] => {
      return getVisibleFields(currentStatus, userRole);
    };

    const getEditableFieldsForRole = (): WorkflowUIField[] => {
      const allEditableFields = getEditableFields(currentStatus, userRole);

      // For mandatory assignment statuses (steps 2, 4, 5, 8), assignment is priority
      if (MANDATORY_ASSIGNMENT_STATUSES.some(status => status === currentStatus)) {
        // Only return editable fields if user is assigned - assignment is mandatory first
        const isAssigned = currentUserId && assignedToUserId && currentUserId === assignedToUserId;
        return isAssigned ? allEditableFields : [];
      }

      return allEditableFields;
    };

    // Debug information
    const isAssigned = currentUserId && assignedToUserId && currentUserId === assignedToUserId;
    const requiresAssignment = MANDATORY_ASSIGNMENT_STATUSES.some(status => status === currentStatus);

    const debug = {
      userRoles: Object.values(UserRole).map(role => role.toString()),
      visibleFields: getVisibleFieldsForRole().map(field => field.toString()),
      editableFields: getEditableFieldsForRole().map(field => field.toString()),
      requiredFields: [], // Required fields logic can be added here if needed
      assignmentStatus: {
        currentUserId,
        assignedToUserId,
        isAssigned,
        requiresAssignment,
        statusRequiresAssignment: requiresAssignment,
        mandatoryAssignmentStatuses: MANDATORY_ASSIGNMENT_STATUSES.map(s => s.toString()),
        assignmentPriority: requiresAssignment && !isAssigned ? 'Assignment required before field editing' : 'Normal permissions'
      }
    };

    return {
      isFieldVisible: isFieldVisibleForRole,
      isFieldEditable: isFieldEditableForRole,
      getFieldPermissions: getFieldPermissionsForRole,
      getVisibleFields: getVisibleFieldsForRole,
      getEditableFields: getEditableFieldsForRole,
      debug
    };
  }, [currentStatus, userRole, currentUserId, assignedToUserId]);
}
